import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {$getRoot, $isElementNode, NodeKey} from 'lexical';
import {useCallback, useEffect} from 'react';

import {EnhancedHeadingNode} from './EnhancedHeadingNode';
import {$createHeadingControlsDecoratorNode} from './HeadingControlsDecoratorNode';
import {isEditable} from './HeadingNodeUtils';

/**
 * HeadingControlsPlugin manages heading controls decorator nodes
 * for template mode functionality.
 */
export const HeadingControlsPlugin = ({isTemplateMode = false}: {isTemplateMode?: boolean}) => {
  const [editor] = useLexicalComposerContext();
  const isEnabled = isEditable();

  const addControlsNodesToHeadings = useCallback(() => {
    if (!isEnabled || !isTemplateMode) {
      return;
    }

    editor.update(() => {
      const root = $getRoot();
      const processedHeadings = new Set<NodeKey>();

      // Recursively find all headings in the document
      const findHeadings = (node: any) => {
        // Only process actual heading elements with proper tags
        if (
          $isElementNode(node) &&
          (node instanceof HeadingNode || node instanceof EnhancedHeadingNode) &&
          node.getTag &&
          ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(node.getTag())
        ) {
          const headingId =
            node instanceof EnhancedHeadingNode ? node.getHeadingId() : node.getKey();

          // Skip if we've already processed this heading
          if (processedHeadings.has(headingId)) {
            return;
          }
          processedHeadings.add(headingId);

          // Check if this heading already has a controls node
          const children = node.getChildren();
          const hasControls = children.some(
            (child) =>
              child.getType() === 'HeadingControls' && (child as any).getHeadingId() === headingId
          );

          // Only add controls if the heading has text content
          const hasTextContent = node.getTextContent().trim().length > 0;

          // Add controls decorator node if not present and has text
          if (!hasControls && hasTextContent) {
            const controlsNode = $createHeadingControlsDecoratorNode(headingId, true); // showChevron = false
            node.append(controlsNode);
          }

          // Remove controls if heading becomes empty
          if (!hasTextContent) {
            const nodeChildren = node.getChildren();
            nodeChildren.forEach((child) => {
              if (child.getType() === 'HeadingControls') {
                child.remove();
              }
            });
          }
        }

        // Recursively check children
        if (node.getChildren) {
          node.getChildren().forEach(findHeadings);
        }
      };

      // Start from root and search recursively
      findHeadings(root);
    });
  }, [editor, isEnabled, isTemplateMode]);

  useEffect(() => {
    // Initial setup
    addControlsNodesToHeadings();

    // Handle when headings are created or changed
    const removeNodeTransform = editor.registerNodeTransform(EnhancedHeadingNode, () => {
      addControlsNodesToHeadings();
    });

    // Listen for editor updates - add controls nodes when needed
    const removeUpdateListener = editor.registerUpdateListener(({dirtyElements, dirtyLeaves}) => {
      // Add controls nodes when there are structural changes
      const hasStructuralChanges = dirtyElements.size > 0 || dirtyLeaves.size > 0;

      if (hasStructuralChanges) {
        addControlsNodesToHeadings();
      }
    });

    return () => {
      removeNodeTransform();
      removeUpdateListener();
    };
  }, [editor, addControlsNodesToHeadings]);

  return null; // This plugin doesn't render anything
};
